import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";

// ----- Tiny per-device session id (persisted so reloads/back-button don't
// inflate the "total visitors" counter). One id per browser, forever.
const SESSION_KEY = "eq_recite_session_id";
const COUNTRY_KEY = "eq_recite_country";
const sessionId = (() => {
  let v = localStorage.getItem(SESSION_KEY);
  if (!v) {
    v = "s-" + (crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36));
    localStorage.setItem(SESSION_KEY, v);
  }
  return v;
})();

// Resolve country via ipapi.co (free, CORS-friendly, ~1k req/day) — but ONLY
// once per browser. Cached value is reused for every subsequent ping so we
// don't hammer the free tier or leak the user's IP on every heartbeat.
const resolveCountry = async () => {
  const cached = localStorage.getItem(COUNTRY_KEY);
  if (cached) {
    try { return JSON.parse(cached); } catch { /* ignore corrupt cache */ }
  }
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 4000);
    const res = await fetch("https://ipapi.co/json/", { signal: ctrl.signal });
    clearTimeout(t);
    if (!res.ok) throw new Error("ipapi non-200");
    const j = await res.json();
    const out = { code: (j.country_code || "ZZ").toUpperCase(), name: j.country_name || "Unknown" };
    localStorage.setItem(COUNTRY_KEY, JSON.stringify(out));
    return out;
  } catch {
    // Fallback — derive a rough region from navigator.language ("en-GB" → "GB").
    const lang = navigator.language || "";
    const m = lang.match(/-([A-Z]{2})/);
    const code = (m?.[1] || "ZZ").toUpperCase();
    return { code, name: code === "ZZ" ? "Unknown" : code };
  }
};

// Heartbeat hook. While `active` is true, pings /recitation/ping every 30 s.
// Stops cleanly when `active` flips false or the component unmounts.
export const useRecitationPing = (active) => {
  const timerRef = useRef(null);
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    const fire = async () => {
      const c = await resolveCountry();
      if (cancelled) return;
      try {
        await api.post("/recitation/ping", {
          session_id: sessionId,
          country_code: c.code,
          country_name: c.name,
        });
      } catch { /* network blip — next tick will retry */ }
    };
    fire(); // immediate ping
    timerRef.current = setInterval(fire, 30_000);
    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
    };
  }, [active]);
};

// Live + lifetime stats polling hook. Polls every `intervalMs` (default 30 s).
export const useRecitationActivity = (intervalMs = 30_000) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const { data: d } = await api.get("/recitation/activity");
        if (!cancelled) setData(d);
      } catch { /* ignore network blip */ }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, intervalMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [intervalMs]);
  return data;
};

// ISO-3166 alpha-2 → flag image URL (flagcdn.com — free, no auth, SVG).
// We dropped the regional-indicator emoji approach because Windows + a few
// Android Chrome builds render them as plain "GB" / "US" text squares.
export const flagUrl = (code, size = 40) => {
  if (!code || code.length !== 2) return null;
  return `https://flagcdn.com/${size}x${Math.round(size * 0.75)}/${code.toLowerCase()}.png`;
};
